import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Snackbar, Alert, Chip } from '@mui/material';
import FlexEditor from './FlexEditor';
import FlexScriptList from './FlexScriptList';
import FlexToolbar from './FlexToolbar';
import FlexOutputConsole from './FlexOutputConsole';
import FlexAiAssistant from './FlexAiAssistant';
import FlexContextPanel from './FlexContextPanel';
import FlexScriptProperties from './FlexScriptProperties';
import useFlexScripts from './hooks/useFlexScripts';
import useFlexExecution from './hooks/useFlexExecution';
import useFlexLsp from './hooks/useFlexLsp';

/**
 * FlexStudio - Main FLEX DSL IDE component
 *
 * Features:
 * - Three-panel layout (scripts list, editor, output)
 * - Syntax highlighting via Monaco
 * - Script CRUD operations
 * - Execution with trace support
 * - Pop-out window capability
 */
function FlexStudio({ themeMode = 'dark', warehouseId = null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isPopout = searchParams.get('popout') === 'true';
  const createScriptName = searchParams.get('createScript'); // Pre-fill script name from URL

  // Clear the createScript param after it's been used
  const handleInitialCreateHandled = useCallback(() => {
    if (createScriptName) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('createScript');
      setSearchParams(newParams, { replace: true });
    }
  }, [createScriptName, searchParams, setSearchParams]);

  // Script management
  const {
    scripts,
    loading: scriptsLoading,
    error: scriptsError,
    selectedScript,
    editorContent,
    isModified,
    isSaving,
    selectScript,
    updateContent,
    createScript,
    saveScript,
    deleteScript,
    updateScriptMeta,
    toggleLock,
    setError: setScriptsError,
  } = useFlexScripts(warehouseId);

  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    try {
      const userJson = localStorage.getItem('waide_user');
      if (userJson) {
        const user = JSON.parse(userJson);
        return user.id || user.email || null;
      }
    } catch (e) {
      console.error('Failed to get user ID:', e);
    }
    return null;
  };
  const currentUserId = getCurrentUserId();

  // Script properties dialog (version/domain/category/extends + input/output schema)
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  // Check if current user can edit the selected script
  const canEditScript = !selectedScript?.isLocked ||
    (currentUserId && selectedScript?.createdBy === currentUserId);

  // Execution management
  const {
    isRunning,
    output,
    diagnostics: executionDiagnostics,
    traceEnabled,
    executeScript,
    toggleTrace,
    clearOutput,
  } = useFlexExecution();

  // LSP connection
  const documentUri = selectedScript?.id ? `flex://${selectedScript.id}` : 'flex://untitled';
  const {
    connected: lspConnected,
    diagnostics: lspDiagnostics,
    didOpen,
    didChange,
  } = useFlexLsp(documentUri);

  // Track document version for LSP
  const documentVersionRef = useRef(1);

  // Merge diagnostics from execution and LSP
  const diagnostics = [...(lspDiagnostics || []), ...(executionDiagnostics || [])];

  // UI state
  const [outputCollapsed, setOutputCollapsed] = useState(false);
  const [aiCollapsed, setAiCollapsed] = useState(false);
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [rightPanelWidth, setRightPanelWidth] = useState(400);
  const [contextPanelHeight, setContextPanelHeight] = useState(180); // Height of input context panel
  const [outputPanelHeight, setOutputPanelHeight] = useState(200); // Height of output panel
  const [executionContext, setExecutionContext] = useState({});
  const [isDraggingVertical1, setIsDraggingVertical1] = useState(false); // Between context and output
  const [isDraggingVertical2, setIsDraggingVertical2] = useState(false); // Between output and AI
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);

  // Show snackbar message (defined early as it's used by other callbacks)
  const showMessage = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Vertical drag handler 1 (between context and output)
  const handleVerticalDrag1Start = useCallback((e) => {
    e.preventDefault();
    setIsDraggingVertical1(true);
  }, []);

  // Vertical drag handler 2 (between output and AI)
  const handleVerticalDrag2Start = useCallback((e) => {
    e.preventDefault();
    setIsDraggingVertical2(true);
  }, []);

  // Horizontal drag handler (for right panel width)
  const handleHorizontalDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDraggingHorizontal(true);
  }, []);

  // Handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      const rightPanel = document.getElementById('flex-right-panel');
      if (!rightPanel) return;
      const rect = rightPanel.getBoundingClientRect();

      if (isDraggingVertical1) {
        const newHeight = e.clientY - rect.top;
        // Constrain between 80px and panel height - 250px (leave room for output + AI)
        const maxHeight = rect.height - 280;
        setContextPanelHeight(Math.max(80, Math.min(newHeight, maxHeight)));
      }

      if (isDraggingVertical2) {
        // Calculate output panel height based on mouse position
        const contextBottom = rect.top + contextPanelHeight + 4; // +4 for first drag bar
        const newOutputHeight = e.clientY - contextBottom;
        // Constrain between 80px and available space minus AI min height
        const maxOutputHeight = rect.height - contextPanelHeight - 180; // Leave 180px for AI
        setOutputPanelHeight(Math.max(80, Math.min(newOutputHeight, maxOutputHeight)));
      }

      if (isDraggingHorizontal) {
        const container = document.getElementById('flex-main-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const newWidth = containerRect.right - e.clientX;
          // Constrain between 300px and 700px
          setRightPanelWidth(Math.max(300, Math.min(newWidth, 700)));
        }
      }
    };

    const handleMouseUp = () => {
      setIsDraggingVertical1(false);
      setIsDraggingVertical2(false);
      setIsDraggingHorizontal(false);
    };

    const isDragging = isDraggingVertical1 || isDraggingVertical2 || isDraggingHorizontal;
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isDraggingHorizontal ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingVertical1, isDraggingVertical2, isDraggingHorizontal, contextPanelHeight]);

  // Handle AI-generated script insertion
  const handleAiInsert = useCallback((generatedScript) => {
    if (editorContent.trim()) {
      // Append to existing content
      updateContent(editorContent + '\n\n' + generatedScript);
    } else {
      // Replace empty content
      updateContent(generatedScript);
    }
    showMessage('AI-generated script inserted', 'success');
  }, [editorContent, updateContent, showMessage]);

  // Notify LSP of document changes
  const handleEditorChange = useCallback((newContent) => {
    updateContent(newContent);
    if (lspConnected) {
      documentVersionRef.current++;
      didChange(documentUri, newContent, documentVersionRef.current);
    }
  }, [updateContent, lspConnected, didChange, documentUri]);

  // Notify LSP when script is selected
  useEffect(() => {
    if (lspConnected && selectedScript && editorContent) {
      documentVersionRef.current = 1;
      didOpen(documentUri, editorContent);
    }
  }, [lspConnected, selectedScript?.id, documentUri, didOpen]); // Don't include editorContent to avoid reopening on every change

  // Handle save
  const handleSave = useCallback(async () => {
    if (!selectedScript) {
      showMessage('No script selected', 'warning');
      return;
    }

    try {
      if (selectedScript.id) {
        // Update existing script
        await saveScript();
        showMessage('Script saved', 'success');
      } else {
        // Create new script
        await createScript({
          name: selectedScript.name || 'Untitled',
          description: selectedScript.description || '',
          scriptBody: editorContent,
        });
        showMessage('Script created', 'success');
      }
    } catch (err) {
      showMessage(`Failed to save: ${err.message}`, 'error');
    }
  }, [selectedScript, editorContent, saveScript, createScript, showMessage]);

  // Handle run
  const handleRun = useCallback(async () => {
    // Prevent double execution
    if (isRunning) {
      console.log('Already running, skipping duplicate execution');
      return;
    }
    if (!editorContent.trim()) {
      showMessage('No script to run', 'warning');
      return;
    }

    try {
      await executeScript(editorContent, executionContext);
      // Output will be shown in console
    } catch (err) {
      // Error already shown in output console
    }
  }, [editorContent, executeScript, executionContext, showMessage, isRunning]);

  // Handle create new script
  const handleCreate = useCallback(async (scriptData) => {
    try {
      await createScript(scriptData);
      showMessage('Script created', 'success');
    } catch (err) {
      showMessage(`Failed to create: ${err.message}`, 'error');
    }
  }, [createScript, showMessage]);

  // Handle delete script
  const handleDelete = useCallback(async (scriptId) => {
    try {
      await deleteScript(scriptId);
      showMessage('Script deleted', 'success');
    } catch (err) {
      showMessage(`Failed to delete: ${err.message}`, 'error');
    }
  }, [deleteScript, showMessage]);

  // Handle update script metadata (name, description)
  const handleUpdateMeta = useCallback(async (scriptId, updates) => {
    try {
      await updateScriptMeta(scriptId, updates);
      showMessage('Script updated', 'success');
    } catch (err) {
      showMessage(`Failed to update: ${err.message}`, 'error');
    }
  }, [updateScriptMeta, showMessage]);

  // Handle toggle lock
  const handleToggleLock = useCallback(async (scriptId) => {
    try {
      const updated = await toggleLock(scriptId);
      showMessage(updated.isLocked ? 'Script locked' : 'Script unlocked', 'success');
    } catch (err) {
      showMessage(`Failed to toggle lock: ${err.message}`, 'error');
    }
  }, [toggleLock, showMessage]);

  // Handle pop-out
  const handlePopOut = useCallback(() => {
    const url = `/flex-ide?popout=true`;
    window.open(url, 'FlexIDE', 'width=1200,height=800,menubar=no,toolbar=no');
  }, []);

  // Handle close (for popout mode)
  const handleClose = useCallback(() => {
    window.close();
  }, []);

  // Clear error after showing
  useEffect(() => {
    if (scriptsError) {
      showMessage(scriptsError, 'error');
      setScriptsError(null);
    }
  }, [scriptsError, showMessage, setScriptsError]);

  // Keyboard shortcuts - only for when editor doesn't have focus
  // Note: Monaco editor already handles Ctrl+S and F5 when editor has focus
  // This handles the case when focus is outside the editor (e.g., in sidebar)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Monaco editor has focus - if so, let Monaco handle it
      const activeElement = document.activeElement;
      const isEditorFocused = activeElement?.closest('.monaco-editor');
      if (isEditorFocused) {
        return; // Let Monaco handle keyboard shortcuts
      }

      // Ctrl+S - Save (when editor not focused)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // F5 - Run (when editor not focused)
      if (e.key === 'F5') {
        e.preventDefault();
        handleRun();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleRun]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      {/* Main content area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left sidebar - Script list (hidden in popout mode) */}
        {!isPopout && (
          <Box sx={{ width: sidebarWidth, flexShrink: 0 }}>
            <FlexScriptList
              scripts={scripts}
              selectedId={selectedScript?.id}
              onSelect={selectScript}
              onCreate={handleCreate}
              onDelete={handleDelete}
              onUpdate={handleUpdateMeta}
              onToggleLock={handleToggleLock}
              currentUserId={currentUserId}
              loading={scriptsLoading}
              initialCreateScriptName={createScriptName}
              onInitialCreateHandled={handleInitialCreateHandled}
            />
          </Box>
        )}

        {/* Main content area - Editor and Right Panel side by side */}
        <Box id="flex-main-container" sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: Code Editor Panel with its own header */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Editor Header/Toolbar */}
            <FlexToolbar
              scriptName={selectedScript?.name}
              isModified={isModified}
              isNewScript={selectedScript && !selectedScript.id}
              isRunning={isRunning}
              isSaving={isSaving}
              traceEnabled={traceEnabled}
              canEdit={canEditScript}
              isLocked={selectedScript?.isLocked}
              onSave={handleSave}
              onRun={handleRun}
              onToggleTrace={toggleTrace}
              onPopOut={handlePopOut}
              onClose={handleClose}
              onOpenProperties={selectedScript ? () => setPropertiesOpen(true) : undefined}
              isPopout={isPopout}
            />

            <FlexScriptProperties
              open={propertiesOpen}
              onClose={() => setPropertiesOpen(false)}
              script={selectedScript}
            />

            {/* Editor */}
            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              <FlexEditor
                value={editorContent}
                onChange={handleEditorChange}
                onSave={handleSave}
                onRun={handleRun}
                diagnostics={diagnostics}
                themeMode={themeMode}
              />
              {/* LSP connection status indicator */}
              <Chip
                size="small"
                label={lspConnected ? 'LSP' : 'LSP Offline'}
                color={lspConnected ? 'success' : 'default'}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  height: 20,
                  fontSize: 10,
                  opacity: 0.7,
                }}
              />
            </Box>
          </Box>

          {/* Horizontal Drag Bar (between editor panel and right panel) */}
          <Box
            onMouseDown={handleHorizontalDragStart}
            sx={{
              width: 4,
              cursor: 'col-resize',
              bgcolor: 'divider',
              '&:hover': {
                bgcolor: 'primary.main',
              },
              transition: 'background-color 0.2s',
            }}
          />

          {/* Right Panel - Input Context, Output Console, AI Assistant */}
          <Box
            id="flex-right-panel"
            sx={{
              width: rightPanelWidth,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderLeft: 1,
              borderColor: 'divider',
            }}
          >
            {/* Section 1: Context Input Panel */}
            <Box
              sx={{
                height: contextCollapsed ? 'auto' : contextPanelHeight,
                minHeight: contextCollapsed ? 32 : 80,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
              }}
            >
              <FlexContextPanel
                context={executionContext}
                onChange={setExecutionContext}
                isCollapsed={contextCollapsed}
                onToggleCollapse={() => setContextCollapsed(!contextCollapsed)}
                warehouseId={warehouseId}
                embedded={true}
              />
            </Box>

            {/* Drag Bar 1 (between context and output) */}
            {!contextCollapsed && (
              <Box
                onMouseDown={handleVerticalDrag1Start}
                sx={{
                  height: 4,
                  cursor: 'row-resize',
                  bgcolor: 'divider',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                  transition: 'background-color 0.2s',
                  flexShrink: 0,
                }}
              />
            )}

            {/* Section 2: Output Console */}
            <Box
              sx={{
                height: outputCollapsed ? 'auto' : outputPanelHeight,
                minHeight: outputCollapsed ? 32 : 80,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
              }}
            >
              <FlexOutputConsole
                output={output}
                isCollapsed={outputCollapsed}
                onToggleCollapse={() => setOutputCollapsed(!outputCollapsed)}
                onClear={clearOutput}
                embedded={true}
              />
            </Box>

            {/* Drag Bar 2 (between output and AI) */}
            {!outputCollapsed && (
              <Box
                onMouseDown={handleVerticalDrag2Start}
                sx={{
                  height: 4,
                  cursor: 'row-resize',
                  bgcolor: 'divider',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                  transition: 'background-color 0.2s',
                  flexShrink: 0,
                }}
              />
            )}

            {/* Section 3: AI Assistant (takes remaining space) */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 150 }}>
              <FlexAiAssistant
                onInsert={handleAiInsert}
                existingScript={editorContent}
                isCollapsed={aiCollapsed}
                onToggleCollapse={() => setAiCollapsed(!aiCollapsed)}
                embedded={true}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FlexStudio;
